/**
 * Element Info Library (web) - mirror of mobile/src/lib/element_info.ts.
 *
 * When the user views ANY plan BEFORE editing (Studio preview, deliverable
 * floor-plans, …) and clicks any element or empty area, an "Info Sheet" opens
 * showing a labelled drawing + a detailed engineering explanation of what the
 * element is, how it is built, and which code references apply.
 *
 * All descriptions are dual-language (ar + en) and shared 1:1 with the mobile
 * app so the two platforms feel like one product.
 */

const IMG_WALL_EXT  = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800";
const IMG_WALL_INT  = "https://images.unsplash.com/photo-1541123437800-1bb1317badc2?w=800";
const IMG_COLUMN    = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800";
const IMG_FOOTING   = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800";
const IMG_SLAB      = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800";
const IMG_DOOR      = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800";
const IMG_WINDOW    = "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800";
const IMG_POOL      = "https://images.unsplash.com/photo-1610164989165-0c4701bc4ce2?w=800";
const IMG_GARDEN    = "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800";
const IMG_COURTYARD = "https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?w=800";

export const ELEMENT_INFO = {
  wall_exterior: {
    id: "wall_exterior", icon: "🧱",
    title_ar: "جدار خارجي", title_en: "Exterior Wall",
    subtitle_ar: "الغلاف الحراري والصوتي للمبنى", subtitle_en: "Building thermal & acoustic envelope",
    image_url: IMG_WALL_EXT,
    sections: [
      { ar: "الوظيفة الإنشائية", en: "Structural Function",
        body_ar: "يحمل جزءاً من أحمال البلاطة إذا كان حاملاً، ويمنع تسرّب الحرارة والرطوبة والصوت. سُمك 20-30 سم في السعودية/الخليج، مع طبقة عزل حراري 5 سم.",
        body_en: "Carries partial slab load if load-bearing; blocks heat/moisture/sound transfer. Typical 20-30 cm thick in KSA/Gulf, with 5 cm thermal insulation layer." },
      { ar: "التركيب النموذجي", en: "Typical Composition",
        body_ar: "من الخارج للداخل: طرطشة اسمنتية ← طوب أحمر/إسمنتي مقاس 20 سم ← صوف صخري 5 سم ← طوب داخلي 10 سم ← لياسة اسمنتية ← دهان.",
        body_en: "Outside → In: cement scratch coat → 20 cm red/concrete block → 5 cm rock wool → 10 cm inner block → plaster → paint." },
      { ar: "الأحمال المرجعية", en: "Design Loads",
        body_ar: "الوزن الذاتي ≈ 3.5 kN/m² للجدار كامل السماكة. مقاومة الرياح تُحسب حسب سرعة الرياح المحلية × معامل التعرّض.",
        body_en: "Self-weight ≈ 3.5 kN/m² for full thickness. Wind resistance per local wind speed × exposure factor." },
    ],
    code_refs: ["SBC-301 §7", "ASHRAE 90.1 (U-value ≤ 0.5 W/m²K)"],
  },
  wall_interior: {
    id: "wall_interior", icon: "🚪",
    title_ar: "جدار داخلي / قاطع", title_en: "Interior / Partition Wall",
    subtitle_ar: "تقسيم الفراغ الداخلي", subtitle_en: "Space subdivision inside the envelope",
    image_url: IMG_WALL_INT,
    sections: [
      { ar: "الوظيفة", en: "Function",
        body_ar: "يفصل الغرف ويوفّر خصوصية بصرية وصوتية. عادةً غير حامل، لكن يجب أن يتحمّل أوزان الخزائن أو الأرفف المعلّقة.",
        body_en: "Separates rooms, provides visual and acoustic privacy. Usually non-load-bearing, but must carry hung cabinets and shelves." },
      { ar: "السماكات المعتادة", en: "Typical Thicknesses",
        body_ar: "10 سم للقواطع البسيطة · 15 سم إذا احتوى تمديدات كهرباء/سباكة · 20 سم إذا فصل بين شقتين (عازل صوتي).",
        body_en: "10 cm simple partitions · 15 cm with routing MEP · 20 cm between apartments (acoustic isolation)." },
    ],
  },
  column: {
    id: "column", icon: "⬛",
    title_ar: "عمود إنشائي", title_en: "Structural Column",
    subtitle_ar: "ينقل الأحمال الرأسية من البلاطات إلى الأساسات", subtitle_en: "Transfers vertical loads from slabs to foundations",
    image_url: IMG_COLUMN,
    sections: [
      { ar: "التصنيف", en: "Classification",
        body_ar: "عمود ركني (2 وجهين خارجيين)، عمود حافة (وجه واحد خارجي)، عمود داخلي. حجمه يعتمد على المساحة المؤثّرة (Tributary Area) × عدد الطوابق.",
        body_en: "Corner (2 exterior faces), edge (1 exterior face), interior. Size depends on tributary area × floor count." },
      { ar: "الأحجام النموذجية", en: "Typical Sizes",
        body_ar: "فيلا طابقين: 25×25 - 35×35 سم · شقة 4 طوابق: 35×35 - 50×50 · فندق 8 طوابق: 60×60 - 80×80.",
        body_en: "2-story villa: 25×25-35×35 cm · 4-story apt: 35×35-50×50 · 8-story hotel: 60×60-80×80." },
      { ar: "التسليح", en: "Reinforcement",
        body_ar: "8 قضبان طولية Ø14-Ø20 مم · كانات Ø8 مم كل 20 سم · نسبة تسليح ρ = 1.5% - 2%.",
        body_en: "8 longitudinal bars Ø14-Ø20 mm · Ø8 mm ties @ 20 cm · steel ratio ρ = 1.5-2%." },
      { ar: "الحمل المصمم عليه", en: "Design Load",
        body_ar: "Ag (سم²) ≈ Load(kN) × 1.2 لخرسانة f'c=25 MPa. تُدوّر لأقرب 5 سم لأعلى.",
        body_en: "Ag (cm²) ≈ Load(kN) × 1.2 for f'c=25 MPa. Round up to nearest 5 cm." },
    ],
    code_refs: ["SBC-304 §10.3", "ACI 318-19 §10"],
  },
  beam: {
    id: "beam", icon: "═",
    title_ar: "كمرة (جسر)", title_en: "Beam",
    subtitle_ar: "ينقل حمل البلاطة إلى الأعمدة", subtitle_en: "Transfers slab loads to columns",
    sections: [
      { ar: "الأنواع", en: "Types",
        body_ar: "كمرة مستطيلة ساقطة (20×50) · كمرة مخفية داخل السقف (20×20 نفس سماكة البلاطة) · كمرة رئيسية × ثانوية.",
        body_en: "Rectangular drop beam (20×50) · Hidden beam inside slab (20×20 same as slab depth) · Primary × Secondary." },
      { ar: "الأبعاد النموذجية", en: "Typical Dimensions",
        body_ar: "عرض = 20-30 سم · عمق = L/10 إلى L/12 حيث L هو البحر بالمتر. مثال: بحر 5 م ← عمق 50 سم.",
        body_en: "Width = 20-30 cm · Depth = L/10 to L/12 where L is span in metres. Example: span 5 m → depth 50 cm." },
    ],
    code_refs: ["SBC-304 §9", "ACI 318-19 §9"],
  },
  footing_isolated: {
    id: "footing_isolated", icon: "🟧",
    title_ar: "قاعدة منفصلة", title_en: "Isolated Footing",
    subtitle_ar: "توزّع حمل العمود على تربة أوسع", subtitle_en: "Spreads column load over wider soil area",
    image_url: IMG_FOOTING,
    sections: [
      { ar: "مبدأ التصميم", en: "Design Principle",
        body_ar: "المساحة المطلوبة A = P / q_allow حيث P حمل العمود بالكيلونيوتن، q_allow قدرة تحمل التربة (150 kN/m² تربة عادية).",
        body_en: "Required area A = P / q_allow where P = column load in kN, q_allow = soil bearing capacity (150 kN/m² typical)." },
      { ar: "الأبعاد النموذجية", en: "Typical Sizes",
        body_ar: "0.8×0.8×0.3 (فيلا خفيف) · 1.5×1.5×0.4 (فيلا 2 طابق) · 2.5×2.5×0.6 (فندق) · 3.5×3.5×0.8 (مول).",
        body_en: "0.8×0.8×0.3 (light villa) · 1.5×1.5×0.4 (2-story villa) · 2.5×2.5×0.6 (hotel) · 3.5×3.5×0.8 (mall)." },
      { ar: "التسليح", en: "Reinforcement",
        body_ar: "شبكتان علوية وسفلية Ø12-Ø16 @ 15 سم في اتجاهين + قضبان انتظار للعمود (Starter Bars).",
        body_en: "Top + bottom mesh Ø12-Ø16 @ 15 cm bothways + column starter bars." },
      { ar: "العمق تحت السطح", en: "Depth Below Grade",
        body_ar: "لا يقل عن 1.2 م تحت أدنى منسوب طبيعي لتفادي التمدّد الحراري والصقيع.",
        body_en: "Minimum 1.2 m below lowest natural grade to avoid thermal/frost movement." },
    ],
    code_refs: ["SBC-303 §5", "ACI 318-19 §13"],
  },
  slab_solid: {
    id: "slab_solid", icon: "▬",
    title_ar: "سقف مصمت", title_en: "Solid Slab",
    subtitle_ar: "بلاطة خرسانية مسلحة كاملة", subtitle_en: "Fully-reinforced solid concrete slab",
    image_url: IMG_SLAB,
    sections: [
      { ar: "المدى الأقصى", en: "Max Span",
        body_ar: "5 م أحادي الاتجاه · 6 م ثنائي الاتجاه · إلى 8 م بلاطة مصمتة سميكة (Flat Slab).",
        body_en: "5 m one-way · 6 m two-way · up to 8 m for a thick flat slab." },
      { ar: "السماكة", en: "Thickness",
        body_ar: "L/25 للبلاطة الأحادية · L/30 للثنائية. الحد الأدنى 15 سم.",
        body_en: "L/25 one-way · L/30 two-way. Absolute min 15 cm." },
    ],
  },
  slab_hordi: {
    id: "slab_hordi", icon: "▨",
    title_ar: "سقف هوردي (بلوك مفرّغ)", title_en: "Hordi (Hollow-Block) Slab",
    subtitle_ar: "بلاطة أخف من المصمتة، أعصاب خرسانية بينها بلوك مفرّغ", subtitle_en: "Lighter than solid - concrete ribs with hollow blocks between",
    sections: [
      { ar: "المدى الأقصى", en: "Max Span",
        body_ar: "7 م بأعصاب واحدة الاتجاه · حتى 9 م بأعصاب متعامدة.",
        body_en: "7 m one-way ribs · up to 9 m two-way ribs." },
      { ar: "الفوائد", en: "Benefits",
        body_ar: "أخف من المصمتة بنسبة 30% · عزل صوتي وحراري أفضل · اقتصادية للأبحر المتوسطة.",
        body_en: "30% lighter than solid · better acoustic + thermal insulation · economical for medium spans." },
    ],
  },
  door: {
    id: "door", icon: "🚪",
    title_ar: "باب", title_en: "Door",
    image_url: IMG_DOOR,
    sections: [
      { ar: "الأنواع والأحجام", en: "Types & Sizes",
        body_ar: "باب رئيسي: 100×230 سم · باب غرفة: 90×210 سم · باب حمام: 80×210 سم · باب خزائن: 60×210 سم.",
        body_en: "Main entrance: 100×230 cm · Room door: 90×210 · Bath: 80×210 · Closet: 60×210." },
      { ar: "قواعد التموضع", en: "Placement Rules",
        body_ar: "لا يفتح على درج مباشرة · لا يواجه المرحاض بشكل مباشر · مسافة 5 سم على الأقل من الركن.",
        body_en: "Never opens directly onto stairs · No direct WC-facing · Minimum 5 cm from corner." },
    ],
    code_refs: ["IBC §1010", "SBC-201"],
  },
  window: {
    id: "window", icon: "🪟",
    title_ar: "نافذة", title_en: "Window",
    image_url: IMG_WINDOW,
    sections: [
      { ar: "نسبة الإضاءة الطبيعية", en: "Natural Light Ratio",
        body_ar: "≥ 10% من مساحة الغرفة للسكن · ≥ 8% للمكاتب. نافذة غرفة 4×4م تحتاج 1.6 م² على الأقل.",
        body_en: "≥ 10% of room area for residential · ≥ 8% offices. A 4×4m room needs ≥ 1.6 m²." },
      { ar: "الارتفاعات النموذجية", en: "Typical Heights",
        body_ar: "غرفة: أسفل النافذة 90 سم من الأرض · مطبخ: 110 سم فوق الطاولة · حمام: 180 سم للخصوصية.",
        body_en: "Bedroom: sill 90 cm from floor · Kitchen: 110 cm above counter · Bath: sill 180 cm for privacy." },
    ],
  },
  pool: {
    id: "pool", icon: "🏊",
    title_ar: "مسبح خارجي", title_en: "Outdoor Swimming Pool",
    subtitle_ar: "هيكل خرساني مسلّح مع نظام تدوير كامل", subtitle_en: "Reinforced concrete shell + complete circulation system",
    image_url: IMG_POOL,
    sections: [
      { ar: "المكوّنات الرئيسية", en: "Main Components",
        body_ar: "1) هيكل خرسانة مسلّحة (15-20 سم للجدران والقاع) · 2) سكيمر واحد أو أكثر لسحب السطح · 3) 4-6 مضخّات إرجاع (Return Jets) على جدران المسبح · 4) مصرف رئيسي (Main Drain) في القاع · 5) غرفة المضخّة والفلتر · 6) شبكة أنابيب PVC.",
        body_en: "1) Reinforced concrete shell (15-20 cm walls & floor) · 2) Skimmer(s) for surface water · 3) 4-6 return jets on side walls · 4) Main drain at floor · 5) Pump/filter room · 6) PVC pipe network." },
      { ar: "مراحل الإنشاء", en: "Construction Phases",
        body_ar: "1. الحفر بعمق 1.6-1.8 م تحت مستوى الأرضية النهائية. 2. صب خرسانة نظافة سماكة 10 سم. 3. تسليح ومسارات أنابيب واقفة قبل الصب. 4. صب الجدران والقاع دفعة واحدة (Monolithic). 5. عزل مائي: مادّة ثنائية المكوّن أو غشاء PVC. 6. تكسية بالبورسلان أو موزاييك 2.5×2.5 سم.",
        body_en: "1. Excavate 1.6-1.8 m below finished floor. 2. Pour 10 cm lean concrete. 3. Rebar + upright pipe stubs before pour. 4. Monolithic pour of walls + floor. 5. Waterproofing: 2-part liquid membrane or PVC liner. 6. Tile with porcelain or 2.5×2.5 cm mosaic." },
      { ar: "مكان ضخّ الماء (المدخل / Return)", en: "Water Inlet (Returns)",
        body_ar: "مضخّات الإرجاع تُثبَّت في جدران المسبح على ارتفاع 30 سم تحت مستوى الماء، بمسافة 4 م بين كل اثنتين. توجَّه فوهاتها بزاوية 45° لتحريك الماء بشكل دائري وضمان توزيع الكلور بالتساوي.",
        body_en: "Return jets installed 30 cm below waterline on side walls, 4 m apart. Nozzles angled 45° to create circular flow ensuring chlorine reaches every spot." },
      { ar: "مكان سحب الماء (Skimmer & Main Drain)", en: "Water Outlet (Skimmer & Main Drain)",
        body_ar: "السكيمر يُثبَّت على مستوى سطح الماء، يجذب 80% من الماء لتنظيف الأوراق والحشرات. المصرف الرئيسي في أعمق نقطة يسحب 20% الباقية من ماء القاع. كلا الخطّين يذهبان إلى غرفة المضخّة.",
        body_en: "Skimmer at waterline pulls 80% of flow (removes leaves + debris). Main drain at deepest point pulls remaining 20% from floor water. Both lines feed the pump room." },
      { ar: "الأبعاد والأعماق النموذجية", en: "Typical Dimensions & Depths",
        body_ar: "مسبح فيلا: 6×3 م، عمق 1.2-1.8 م تدريجي · مسبح عائلي: 8×4 م، عمق موحّد 1.4 م · بركة أطفال منفصلة: 3×2 م، عمق 0.4 م.",
        body_en: "Villa pool: 6×3 m, graded 1.2-1.8 m deep · Family pool: 8×4 m, uniform 1.4 m · Kids' plunge: 3×2 m, 0.4 m deep." },
    ],
    code_refs: ["ANSI/APSP-7 (Suction Entrapment)", "ISPSC 2021", "SASO 3452"],
  },
  formwork: {
    id: "formwork", icon: "🪵",
    title_ar: "النجارة (الشدة الخشبية)", title_en: "Formwork (Carpentry)",
    subtitle_ar: "الفرم الخشبية قبل الصب وجدول الفك بعده", subtitle_en: "Timber shutters before the pour + striking schedule after",
    image_url: IMG_FOOTING,
    sections: [
      { ar: "قبل الصب - تركيب الشدة", en: "Before pour - erection",
        body_ar: "خنزيرة القواعد (ألواح جانبية + قوائم وتربيط)، شدة رقاب وأعمدة، شدة الميدات والكمرات، وشدة أسقف البلاطات بقوائم إسناد. يجب ضبط الاستقامة والمنسوب وإحكام الوصلات ودهان زيت الفك قبل صب الخرسانة.",
        body_en: "Footing shutters (side boards + props/braces), column-neck & column boxes, tie/floor-beam shutters, and slab soffit on shoring props. Check line, level, joint tightness and apply release oil before pouring." },
      { ar: "بعد الصب - فك الشدة", en: "After pour - striking",
        body_ar: "الأوجه الرأسية (جوانب القواعد/الأعمدة/الكمرات): 24–48 ساعة. بطن البلاطة (مع إبقاء القوائم): 7 أيام. بطن الكمرة: 14 يوماً. إزالة القوائم/إعادة الإسناد: 14–21 يوماً - تقديرات تُعدّل حسب الخلطة والحرارة والبحور الفعلية.",
        body_en: "Vertical faces (footing/column/beam sides): 24–48 h. Slab soffit (props kept): 7 d. Beam soffit: 14 d. Remove props / reshore: 14–21 d - adjust to mix, temperature and actual spans." },
    ],
    code_refs: ["ACI 347R (Formwork)", "EN 13670 (Execution)", "SBC 304"],
  },
  rebar: {
    id: "rebar", icon: "🧷",
    title_ar: "حديد التسليح", title_en: "Reinforcement (Rebar)",
    subtitle_ar: "قفص التسليح وجدول ثني الحديد حسب الكود المختار", subtitle_en: "Rebar cage + bar-bending schedule per the selected code",
    image_url: IMG_COLUMN,
    sections: [
      { ar: "المبدأ", en: "Principle",
        body_ar: "الحديد يقاوم الشد والخرسانة تقاوم الضغط. القضبان الطولية + الكانات تُشكّل القفص مع مراعاة الغطاء الخرساني وأطوال الوصل والثني حسب الكود.",
        body_en: "Steel carries tension, concrete carries compression. Longitudinal bars + stirrups form the cage with correct cover, lap and bend lengths per code." },
      { ar: "تنبيه سلامة إلزامي", en: "Mandatory safety note",
        body_ar: "الكميات وأقطار الحديد تقديرية للاسترشاد فقط. لا تُنفَّذ إلا بعد مراجعة واعتماد مهندس إنشائي مرخّص، بناءً على الأحمال الفعلية وتقرير جسّ التربة وظروف الموقع.",
        body_en: "Quantities and bar sizes are estimates for guidance only. Do NOT build from them until reviewed and stamped by a licensed structural engineer based on actual loads, a soil report and site conditions." },
    ],
    code_refs: ["SBC 304 §5", "ACI 318-19", "EN 1992-1-1"],
  },
  garden: {
    id: "garden", icon: "🌳",
    title_ar: "حديقة", title_en: "Garden",
    subtitle_ar: "منطقة خضراء لتحسين المناخ الداخلي والخارجي", subtitle_en: "Green zone improving indoor + outdoor climate",
    image_url: IMG_GARDEN,
    sections: [
      { ar: "التربة والتصريف", en: "Soil & Drainage",
        body_ar: "طبقة تصريف حصى 20 سم أسفل التربة الزراعية · نسيج جيوتكستايل يمنع الاختلاط · تربة زراعية 40-60 سم مع نسبة تسميد 30%.",
        body_en: "20 cm gravel drainage layer under topsoil · geotextile between layers · 40-60 cm planting soil with 30% compost." },
      { ar: "الري", en: "Irrigation",
        body_ar: "أنابيب PE 16 مم بالتنقيط لكل نبتة · شبكة رشّاشات للعشب · مؤقّت أوتوماتيك ثلاث دورات يومياً في الصيف.",
        body_en: "PE 16 mm drip lines per plant · sprinkler network for turf · auto-timer, 3 cycles/day in summer." },
    ],
  },
  courtyard: {
    id: "courtyard", icon: "⛲",
    title_ar: "فناء داخلي", title_en: "Interior Courtyard",
    subtitle_ar: "فراغ مكشوف داخل المبنى للإضاءة والتهوية الطبيعية", subtitle_en: "Open-air space inside the building for natural light + ventilation",
    image_url: IMG_COURTYARD,
    sections: [
      { ar: "الوظيفة الحرارية", en: "Thermal Function",
        body_ar: "يعمل كمدخنة شمسية: الهواء الساخن يصعد للأعلى ويُسحَب من فتحات علوية، مما يخفض درجة الحرارة الداخلية 4-6 °C دون تكييف.",
        body_en: "Acts as a solar chimney: hot air rises + escapes through top vents, dropping interior temp 4-6 °C without HVAC." },
      { ar: "الأبعاد الدنيا", en: "Minimum Sizes",
        body_ar: "لا يقل عرضه عن 1/3 ارتفاع المبنى المحيط به. لفيلا طابقين (6م) ← 2×2 م على الأقل.",
        body_en: "Width ≥ 1/3 building height. 2-story villa (6m) → 2×2 m minimum." },
    ],
  },
  balcony: {
    id: "balcony", icon: "🌅",
    title_ar: "شرفة / بلكونة", title_en: "Balcony",
    sections: [
      { ar: "الأبعاد الآمنة", en: "Safe Dimensions",
        body_ar: "العمق الأدنى المفيد 1.2 م لوضع كرسي · الحاجز 1.1 م ارتفاع · فتحات ≤ 10 سم لحماية الأطفال.",
        body_en: "Minimum useful depth 1.2 m for a chair · Guard rail 1.1 m tall · Openings ≤ 10 cm for child safety." },
    ],
    code_refs: ["IBC §1015.3", "SBC-201"],
  },
  terrace: {
    id: "terrace", icon: "☀️",
    title_ar: "تراس أرضي", title_en: "Ground Terrace",
    sections: [
      { ar: "التركيب", en: "Composition",
        body_ar: "بلاطة خرسانية مسلحة على ركيزة أرضية · عزل مائي 3 مم · طبقة ميلان 2% باتجاه المصرف · تكسية بلاط أو خشب.",
        body_en: "Slab-on-grade RC · 3 mm waterproofing · 2% slope to drain · tile or wood finish." },
    ],
  },
  light_well: {
    id: "light_well", icon: "🌬️",
    title_ar: "منور تهوية", title_en: "Ventilation Light-Well",
    subtitle_ar: "فتحة رأسية مفتوحة للسماء تُدخل الضوء والهواء للغرف الداخلية", subtitle_en: "Vertical shaft open to sky bringing daylight + air to interior rooms",
    sections: [
      { ar: "الأبعاد الهندسية", en: "Engineering Sizes",
        body_ar: "منور 1×1 م يخدم غرفة داخلية واحدة، و 2×1 م مشترك بين غرفتين متجاورتين (مثل مطبخ ↔ حمام). يوضع على الجدار الفاصل ليخدم الفراغين معاً.",
        body_en: "1×1 m well serves ONE interior room; 2×1 m shared between two adjacent rooms (e.g. kitchen ↔ bath). Placed on the shared partition so it serves both spaces." },
      { ar: "الإضاءة والتهوية", en: "Light & Ventilation",
        body_ar: "شباك على كل غرفة محيطة يفتح على المنور للإضاءة والتهوية المتبادلة · يعمل كمدخنة شمسية تسحب الهواء الساخن للأعلى.",
        body_en: "A window from each surrounding room opens onto the well for daylight + cross-ventilation · acts as a solar chimney drawing hot air up." },
    ],
    code_refs: ["IBC §1204 - Light wells", "SBC 201"],
  },
  // ── functional spaces (rooms & circulation) ──
  room: {
    id: "room", icon: "🛏️",
    title_ar: "غرفة / فراغ", title_en: "Room / Space",
    subtitle_ar: "فراغ داخلي وظيفي ضمن المخطط", subtitle_en: "A functional interior space in the plan",
    sections: [
      { ar: "الخصوصية والحركة", en: "Privacy & Circulation",
        body_ar: "يُوصل لكل غرفة عبر ممر أو صالة دون المرور خلال غرفة أخرى - عدا حمام الماستر الذي يُدخَل من غرفة الماستر فقط. الأبواب لا تتقابل مباشرة حفاظاً على الخصوصية.",
        body_en: "Every room is reached via a corridor or hall without passing through another room - except the master bath, entered from the master bedroom only. Doors avoid facing each other for privacy." },
      { ar: "الإضاءة والتهوية", en: "Light & Ventilation",
        body_ar: "لكل فراغ نافذة خارجية أو منور. مساحة النوافذ ≥ 10% من مساحة الأرضية للسكن. الغرف الداخلية تُخدَم بمنور مفتوح للسماء.",
        body_en: "Each space has an exterior window or a light-well. Glazing ≥ 10% of floor area for residential. Interior rooms are served by an open-to-sky light-well." },
      { ar: "الأبعاد المريحة", en: "Comfortable Sizes",
        body_ar: "غرفة نوم رئيسية (ماستر) 16-20 م² · غرفة عادية 12-14 م² · غرفة أطفال 9-11 م² · حمام 3-5 م² · مطبخ 8-12 م².",
        body_en: "Master bedroom 16-20 m² · Regular bedroom 12-14 m² · Kids room 9-11 m² · Bathroom 3-5 m² · Kitchen 8-12 m²." },
    ],
  },
  corridor: {
    id: "corridor", icon: "↔️",
    title_ar: "ممر / موزّع حركة", title_en: "Corridor / Circulation",
    subtitle_ar: "يربط المدخل بكل الغرف دون اختراق الفراغات الخاصة", subtitle_en: "Links the entrance to every room without cutting through private spaces",
    sections: [
      { ar: "الوظيفة", en: "Function",
        body_ar: "العمود الفقري للحركة داخل المبنى. يوصل من المدخل/الصالة إلى الغرف والحمامات بحيث لا يُضطر أحد للمرور عبر غرفة للوصول لأخرى.",
        body_en: "The building's movement spine. Connects the entrance/hall to rooms and baths so nobody must pass through one room to reach another." },
      { ar: "العرض المطلوب", en: "Required Width",
        body_ar: "لا يقل صافي العرض عن 0.9 م للسكن، و 1.2 م للمباني العامة، ويُفضَّل 1.1 م لتمرير الأثاث بأريحية.",
        body_en: "Clear width ≥ 0.9 m residential, ≥ 1.2 m public buildings; 1.1 m preferred to move furniture comfortably." },
    ],
    code_refs: ["IBC §1020", "SBC-201"],
  },
  shop: {
    id: "shop", icon: "🏬",
    title_ar: "محل تجاري", title_en: "Retail Shop",
    subtitle_ar: "وحدة تجارية على الواجهة", subtitle_en: "Commercial unit facing the street",
    sections: [
      { ar: "الأبعاد النموذجية", en: "Typical Sizes",
        body_ar: "واجهة 4-6 م × عمق 6-12 م · ارتفاع صافٍ ≥ 3.5 م · باب واجهة زجاجي 3-4 م + دورة مياه خلفية.",
        body_en: "4-6 m frontage × 6-12 m depth · clear height ≥ 3.5 m · 3-4 m glazed shopfront + rear WC." },
    ],
  },
  parking: {
    id: "parking", icon: "🅿️",
    title_ar: "موقف سيارات", title_en: "Parking",
    subtitle_ar: "مكان وقوف السيارات", subtitle_en: "Vehicle parking bay",
    sections: [
      { ar: "الأبعاد", en: "Dimensions",
        body_ar: "موقف عمودي 2.5×5.0 م · ممر مناورة 6.0 م · موقف ذوي الاحتياجات 3.6×5.0 م.",
        body_en: "Perpendicular bay 2.5×5.0 m · maneuvering aisle 6.0 m · accessible bay 3.6×5.0 m." },
    ],
  },
  stair: {
    id: "stair", icon: "🪜",
    title_ar: "درج / سلّم", title_en: "Staircase",
    subtitle_ar: "الحركة الرأسية بين الطوابق", subtitle_en: "Vertical movement between floors",
    sections: [
      { ar: "أبعاد الدرجة", en: "Step Geometry",
        body_ar: "الارتفاع (القائمة) 17-18 سم · العرض (النائمة) 28-30 سم · قاعدة 2×القائمة + النائمة = 60-64 سم.",
        body_en: "Riser 17-18 cm · tread 28-30 cm · rule 2×riser + tread = 60-64 cm." },
      { ar: "العرض والاستراحة", en: "Width & Landing",
        body_ar: "عرض القلبة ≥ 0.9 م سكني (1.2 م عام) · استراحة (بسطة) بعد كل 8-12 درجة · حاجز 0.9-1.1 م.",
        body_en: "Flight width ≥ 0.9 m residential (1.2 m public) · landing after every 8-12 steps · handrail 0.9-1.1 m." },
    ],
    code_refs: ["IBC §1011", "SBC-201"],
  },
  entry: {
    id: "entry", icon: "🚪",
    title_ar: "المدخل الرئيسي", title_en: "Main Entrance",
    subtitle_ar: "نقطة الدخول والواجهة الأمامية", subtitle_en: "Entry point on the front facade",
    sections: [
      { ar: "الموقع", en: "Location",
        body_ar: "السهم يشير إلى اتجاه الدخول، ويقع على الواجهة الأمامية المطلّة على الشارع. يفتح على صالة/موزّع يوصل لبقية الفراغات.",
        body_en: "The arrow shows the entry direction on the street-facing front facade. It opens onto a hall/distributor that reaches the rest of the spaces." },
      { ar: "الأبعاد", en: "Dimensions",
        body_ar: "باب رئيسي 1.0-1.2 م عرض × 2.3 م ارتفاع · يفضَّل مظلّة/بسطة مدخل 1.5×1.5 م.",
        body_en: "Main door 1.0-1.2 m wide × 2.3 m tall · canopy/landing 1.5×1.5 m preferred." },
    ],
  },
};

// General plan explanation shown when the user clicks empty area / the plot.
export const GENERAL_PLAN_INFO = {
  id: "plan", icon: "📐",
  title_ar: "قراءة المخطط", title_en: "Reading the Plan",
  subtitle_ar: "اضغط أي عنصر لعرض رسمه وشرحه الكامل", subtitle_en: "Tap any element to see its drawing + full explanation",
  sections: [
    { ar: "قطعة الأرض والارتدادات", en: "Plot & Setbacks",
      body_ar: "الخط الأحمر المتقطّع هو حد الأرض، والشريط المُظلَّل حوله هو الارتداد عن الجيران. المبنى يقع داخل هذه الحدود مع مراعاة العمق والواجهة والجيران.",
      body_en: "The dashed red line is the plot boundary; the hatched strip around it is the setback from neighbours. The building sits inside these limits respecting depth, facade and neighbours." },
    { ar: "الحركة والخصوصية", en: "Circulation & Privacy",
      body_ar: "الحركة تمر عبر الصالة والممرات لتصل لكل غرفة دون اختراق فراغ آخر - عدا حمام الماستر الذي يُدخَل من الماستر. السهم الأحمر يوضّح المدخل الرئيسي.",
      body_en: "Movement flows through the hall and corridors to reach every room without cutting through another space - except the master bath, entered from the master bedroom. The red arrow marks the main entrance." },
    { ar: "الإضاءة والتهوية", en: "Light & Ventilation",
      body_ar: "كل فراغ داخلي محجوب يُخدَم بمنور مفتوح للسماء (بالمساحات المكتوبة) لا يتداخل مع الغرف أو الممرات أو الشرفات.",
      body_en: "Every blocked interior space is served by an open-to-sky light-well (areas labelled) that does not overlap rooms, corridors or terraces." },
    { ar: "القياسات", en: "Dimensions",
      body_ar: "سلاسل القياس على الأطراف تعطي طول كل جزء من الجدار والبعد الإجمالي. فعّل زر القياسات لعرضها.",
      body_en: "Dimension chains along the edges give every wall segment length and the overall span. Toggle the dimensions button to show them." },
  ],
};

/** Map a plan zone/label into an ELEMENT_INFO key. */
export function zoneToInfoKind(zoneKind, label) {
  const k = String(zoneKind || label || "").toLowerCase();
  if (/vent|منور|light.?well|shaft|تهوية/.test(k)) return "light_well";
  if (/pool|مسبح/.test(k)) return "pool";
  if (/garden|حديقة/.test(k)) return "garden";
  if (/courtyard|فناء/.test(k)) return "courtyard";
  if (/balcony|بلكون/.test(k)) return "balcony";
  if (/terrace|شرفة|تراس/.test(k)) return "terrace";
  if (/corridor|hall|distributor|ممر|بهو|موزع|ردهة|توزيع|صالة/.test(k)) return "corridor";
  if (/shop|store|محل|دكان|متجر/.test(k)) return "shop";
  if (/parking|موقف|جراج|كراج/.test(k)) return "parking";
  if (/stair|درج|سلم|سلّم/.test(k)) return "stair";
  return "room";
}

/** Lookup helper - pass an element type string and get its info. */
export function getElementInfo(kind) {
  if (!kind) return null;
  const k = String(kind).toLowerCase();
  if (ELEMENT_INFO[k]) return ELEMENT_INFO[k];
  if (k.includes("exterior") || k === "wall") return ELEMENT_INFO.wall_exterior;
  if (k.includes("interior") || k.includes("partition")) return ELEMENT_INFO.wall_interior;
  if (k.startsWith("footing")) return ELEMENT_INFO.footing_isolated;
  if (k.startsWith("slab_solid")) return ELEMENT_INFO.slab_solid;
  if (k.startsWith("hordi") || k.includes("ribbed")) return ELEMENT_INFO.slab_hordi;
  if (k.includes("pool")) return ELEMENT_INFO.pool;
  if (k.includes("formwork") || k.includes("carpentry") || k.includes("shutter")) return ELEMENT_INFO.formwork;
  if (k.includes("rebar") || k.includes("reinforc") || k.includes("steel")) return ELEMENT_INFO.rebar;
  if (k === "garden") return ELEMENT_INFO.garden;
  if (k === "courtyard") return ELEMENT_INFO.courtyard;
  if (k === "vent" || k.includes("light_well") || k.includes("light-well") ||
      k.includes("ventilation") || k.includes("shaft") || k.includes("منور")) return ELEMENT_INFO.light_well;
  return null;
}
